import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { transfersService } from '../services/transfersService';

interface ExcelImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'upload' | 'preview' | 'result';

interface ParsedRow {
  rowNumber: number;
  data: {
    tripType: string;
    transferDate: string;
    transferTime: string;
    pickupLocation: string;
    dropoffLocation: string;
    flightCode?: string;
    passengerCount: number;
    guestNames?: string;
    customerName?: string;
    customerPrice?: number;
    providerPrice?: number;
    currency?: string;
    specialNotes?: string;
  };
  isValid: boolean;
  errors: string[];
}

interface ImportResult {
  created: number;
  failed: number;
  errors: { row: number; message: string }[];
}

export const ExcelImportModal: React.FC<ExcelImportModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('upload');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [validCount, setValidCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const resetState = () => {
    setStep('upload');
    setLoading(false);
    setRows([]);
    setValidCount(0);
    setErrorCount(0);
    setResult(null);
    setDragOver(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    try {
      await transfersService.downloadImportTemplate();
      toast.success(t('transfers.templateDownloaded'));
    } catch {
      toast.error(t('transfers.templateDownloadFailed'));
    }
  };

  const handleFileSelect = async (file: File) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error(t('transfers.onlyExcelFiles'));
      return;
    }

    setLoading(true);
    try {
      const data = await transfersService.previewImportExcel(file);
      setRows(data.rows);
      setValidCount(data.validCount);
      setErrorCount(data.errorCount);
      setStep('preview');
    } catch (error: any) {
      const msg = error.response?.data?.message || t('transfers.parseError');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleExecuteImport = async () => {
    const validRows = rows.filter(r => r.isValid).map(r => r.data);
    if (validRows.length === 0) return;

    setLoading(true);
    try {
      const res = await transfersService.executeImport(validRows);
      setResult(res);
      setStep('result');
      if (res.created > 0) {
        onSuccess();
      }
    } catch (error: any) {
      const msg = error.response?.data?.message || t('transfers.importFailed');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
            {t('transfers.bulkImport')}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              {/* Template Download */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">
                  {t('transfers.step1Template')}
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-400 mb-3">
                  {t('transfers.templateDescription')}
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {t('transfers.downloadTemplate')}
                </button>
              </div>

              {/* File Upload */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  {t('transfers.step2Upload')}
                </h3>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    dragOver
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
                >
                  {loading ? (
                    <div className="text-gray-500 dark:text-gray-400">
                      <svg className="animate-spin h-8 w-8 mx-auto mb-3 text-blue-500" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      {t('transfers.parsing')}
                    </div>
                  ) : (
                    <>
                      <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="text-gray-600 dark:text-gray-400 font-medium">{t('transfers.dragDropExcel')}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">.xlsx</p>
                    </>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('transfers.totalRows')}:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{rows.length}</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                  <span className="text-sm text-green-700 dark:text-green-400">{t('transfers.validRows')}:</span>
                  <span className="font-semibold text-green-800 dark:text-green-300">{validCount}</span>
                </div>
                {errorCount > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                    <span className="text-sm text-red-700 dark:text-red-400">{t('transfers.errorRows')}:</span>
                    <span className="font-semibold text-red-800 dark:text-red-300">{errorCount}</span>
                  </div>
                )}
              </div>

              {/* Table */}
              <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">#</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('transfers.status')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('transfers.direction')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('transfers.date')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('transfers.time')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('transfers.pickup')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('transfers.dropoff')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('transfers.passengers')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">{t('transfers.error')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((row, idx) => (
                      <tr
                        key={idx}
                        className={row.isValid
                          ? 'bg-green-50 dark:bg-green-900/10'
                          : 'bg-red-50 dark:bg-red-900/10'
                        }
                      >
                        <td className="px-3 py-2 text-gray-500">{row.rowNumber}</td>
                        <td className="px-3 py-2">
                          {row.isValid ? (
                            <span className="text-green-600 font-bold">&#10003;</span>
                          ) : (
                            <span className="text-red-600 font-bold">&#10007;</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">{row.data.tripType === 'arrival' ? t('transfers.arrival') : row.data.tripType === 'departure' ? t('transfers.departure') : row.data.tripType}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">{row.data.transferDate}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">{row.data.transferTime}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white max-w-[150px] truncate">{row.data.pickupLocation}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white max-w-[150px] truncate">{row.data.dropoffLocation}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">{row.data.passengerCount}</td>
                        <td className="px-3 py-2 text-red-600 dark:text-red-400 text-xs">
                          {row.errors.join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Step 3: Result */}
          {step === 'result' && result && (
            <div className="space-y-4">
              {/* Success */}
              {result.created > 0 && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 text-xl">&#10003;</span>
                    <span className="text-green-800 dark:text-green-300 font-semibold">
                      {t('transfers.importSuccess', { count: result.created })}
                    </span>
                  </div>
                </div>
              )}

              {/* Errors */}
              {result.failed > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-red-800 dark:text-red-300 font-semibold mb-2">
                    {result.failed} {t('transfers.rowsFailed')}
                  </p>
                  <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
                    {result.errors.map((err, idx) => (
                      <li key={idx}>{t('transfers.rowError', { row: err.row, message: err.message })}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700">
          {step === 'upload' && (
            <button
              onClick={handleClose}
              className="px-4 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 font-medium min-h-[44px]"
            >
              {t('common.cancel')}
            </button>
          )}

          {step === 'preview' && (
            <>
              <button
                onClick={() => { resetState(); }}
                className="px-4 py-2.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 font-medium min-h-[44px]"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleExecuteImport}
                disabled={validCount === 0 || loading}
                className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed min-h-[44px]"
              >
                {loading ? t('transfers.importInProgress') : t('transfers.createValidOnes', { count: validCount })}
              </button>
            </>
          )}

          {step === 'result' && (
            <button
              onClick={handleClose}
              className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium min-h-[44px]"
            >
              {t('common.close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
